/**
 * EventPickerCard — filterable list of MM events proxied through the backend.
 * Single select; clicking a row calls onPick and moves into plan-pick.
 */

import { useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import type { MmOption } from '../api'
import { AgentBubble } from './AgentBubble'

interface EventPickerCardProps {
  events: MmOption[]
  busy: boolean
  onPick: (ev: MmOption) => void
}

export function EventPickerCard({ events, busy, onPick }: EventPickerCardProps) {
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return events
    return events.filter((e) => e.name.toLowerCase().includes(q))
  }, [events, query])

  return (
    <div className="w-full max-w-xl space-y-4">
      <AgentBubble from="agent">
        Which event?
      </AgentBubble>

      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events…"
            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-carnival-400 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          />
        </div>

        <ul className="max-h-72 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-700">
          {filtered.length === 0 ? (
            <li className="px-2 py-3 text-xs text-slate-500 dark:text-slate-400">
              No events match "{query}".
            </li>
          ) : (
            filtered.map((ev) => (
              <li key={ev.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onPick(ev)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm text-slate-800 transition hover:bg-carnival-50 disabled:opacity-50 dark:text-slate-100 dark:hover:bg-carnival-400/10"
                >
                  <span>{ev.name}</span>
                  <span className="text-[10px] text-slate-400">#{ev.id}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}
